import { GetRef, Input, Popover } from 'antd';
import {
  useRef, useState, useEffect, ChangeEvent, ComponentProps,
} from 'react';
import { useTranslation } from 'react-i18next';
import { IconSvg } from 'shared/icon';
import { StyledTextareaControls, StyledTextareaTag, StyledTextareaWrapper } from 'components/communication/Communication.styled';
import { FormInputWrapper } from 'components/styledComponents/input/Input.styled';
import { decodeHtml } from 'utils/utils';
import { StyledCustomSelect, StyledCustomSelectOption } from 'components/styledComponents/select/Select.styled';
import { Tags } from './types';

const { TextArea } = Input;

type TextAreaProps = ComponentProps<typeof TextArea>;
type TextAreaRef = GetRef<typeof TextArea>;

interface PlainTextProp extends TextAreaProps {
  tags?: Tags;
  message?: string;
  setMessage?: (message: string) => void;
}

const PlainText = ({
  tags, message: messageProp, setMessage: setMessageProp, ...rest
}: PlainTextProp) => {
  const [message, setMessage] = useState(messageProp ? decodeHtml(messageProp) : '');
  const [popoverVisible, setPopoverVisible] = useState(false);

  const onVisibleChange = () => {
    setPopoverVisible(!popoverVisible);
  };

  const ref = useRef<TextAreaRef>(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (messageProp !== message) {
      setMessageProp?.(message);
    }
  }, [message, messageProp, setMessageProp]);

  const selectTag = (tag: string) => {
    ref.current?.focus();
    setMessage((prev) => prev.slice(0, ref.current?.resizableTextArea?.textArea.selectionStart) + tag + prev.slice(ref.current?.resizableTextArea?.textArea?.selectionStart));
    setPopoverVisible(false);
  };

  const renderContent = () => (
    <StyledCustomSelect>
      {tags?.map(({ key }) => (
        <StyledCustomSelectOption key={key} onClick={() => selectTag(key)}>
          <IconSvg icon="CopyTagIcon" width="1.71rem" height="1.71rem" color="" />
          {key}
        </StyledCustomSelectOption>
      ))}
    </StyledCustomSelect>
  );

  const onChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = e.target;
    setMessage(value);
  };

  return (
    <>
      <StyledTextareaControls>
        <Popover placement="bottomLeft" content={renderContent()} title="" trigger="click" open={popoverVisible} onOpenChange={onVisibleChange}>
          <StyledTextareaTag>
            <IconSvg icon="EditorTagsIcon" width="1.27rem" />
            <span>{t('tags')}</span>
            <IconSvg icon="DropdownIcon" width="0.71rem" color="" />
          </StyledTextareaTag>
        </Popover>
      </StyledTextareaControls>
      <StyledTextareaWrapper>
        <FormInputWrapper>
          <TextArea defaultValue={messageProp} ref={ref} autoSize={{ minRows: 15, maxRows: 15 }} value={message} onChange={onChange} {...rest} />
        </FormInputWrapper>
      </StyledTextareaWrapper>
    </>
  );
};

export default PlainText;
